import React, { Key, useState } from 'react';
import styled from 'styled-components';
import { Input, Icon, Checkbox, Space, Button, Divider } from 'components';
import { FilterDropdownProps } from 'antd/lib/table/interface';

const EnhancedFilterDropdown: React.FC<
  FilterDropdownProps & {
    allowSearch?: boolean;
    okText?: string;
    resetText?: string;
  }
> = ({
  confirm,
  selectedKeys,
  setSelectedKeys,
  filters,
  visible,
  allowSearch,
  okText,
  resetText,
}) => {
  const [selectedValues, setSelectedValues] = useState<Set<Key>>(
    new Set(selectedKeys),
  );
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleSubmit = () => {
    setSelectedKeys([...selectedValues]);
    confirm();
  };

  React.useEffect(() => {
    if (!visible) setSelectedValues(new Set(selectedKeys));
  }, [visible]);

  return (
    <StyledFilter direction="vertical">
      {allowSearch && (
        <Input
          placeholder="Search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          suffix={<Icon type="search" color="gray5" />}
        />
      )}
      <StyledOptions direction="vertical">
        {filters
          ?.filter(
            ({ text }) =>
              typeof text !== 'string' ||
              text?.toLowerCase().includes(searchTerm.toLowerCase()),
          )
          .map(({ text, value }) => (
            <Checkbox
              key={value.toString()}
              checked={selectedValues.has(value.toString())}
              onChange={e => {
                setSelectedValues((prev: Set<Key>) => {
                  const newSelection = new Set(prev);
                  if (e.target.checked) newSelection.add(value.toString());
                  if (!e.target.checked) newSelection.delete(value.toString());
                  return newSelection;
                });
              }}
              data-cy={`filter-option-${
                typeof text === 'string' ? text : value.toString()
              }`}
            >
              {text}
            </Checkbox>
          ))}
      </StyledOptions>
      <StyledDivider />
      <StyledButtonWrapper>
        <Button
          type="link"
          size="small"
          disabled={selectedValues.size === 0}
          onClick={() => {
            setSelectedValues(new Set());
            if (allowSearch) setSearchTerm('');
          }}
        >
          {resetText ?? 'Clear'}
        </Button>
        <Button type="primary" size="small" onClick={handleSubmit}>
          {okText ?? 'Save'}
        </Button>
      </StyledButtonWrapper>
    </StyledFilter>
  );
};

export default EnhancedFilterDropdown;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  ${Button} {
    border-radius: 2px;
  }
`;

const StyledFilter = styled(Space)`
  width: min-content;
  min-width: 178px;
  padding: 10px;
  max-width: 300px;
`;

const StyledOptions = styled(Space)`
  overflow-y: auto;
  max-height: 30vh;
  width: 100%;
  white-space: pre;

  // this prevents scrollbar from flickering when selecting the last item.
  padding-bottom: 2px;
  margin-bottom: -2px;
`;
